import { DateTime } from 'luxon-business-days';

export const DATE_FORMAT = {
  // Date format used on the UI. Example: Oct 30, 2024
  READABLE: 'MMM dd, yyyy',
  // Date format used on the UI. Example: Oct 30
  READABLE_SHORT: 'MMM dd',
  // Date format used in the US. Example: 10/30/2024
  US_SHORT: 'MM/dd/yyyy',
  // ISO 8601 date only string. Example: 2024-10-30
  ISO: 'yyyy-MM-dd',
}

/**
 * @returns {string} Today's date only as an ISO 8601-compliant string
 * @example
 * getTodayIsoDate() => '2024-10-30'
 */
export const getTodayIsoDate = () => {
  const localNow = DateTime.now();

  const localDateOnlyString = localNow.toISODate();
  return localDateOnlyString;
}

/**
 * @returns {Date} Current local date and time as a JavaScript Date object
 */
export const getJsNow = () => {
  const localNow = DateTime.now();

  const localJsNowDateTime = localNow.toJSDate();
  return localJsNowDateTime;
}

/**
 * @param {string} utcIsoDateTimeString
 * @returns {Date | null} Local date and time as a JavaScript Date object
 * @example
 * toJsDateTime('2024-10-30T00:00:00.000Z') => new Date('2024-10-30T00:00:00.000Z')
 */
export const toJsDateTime = (utcIsoDateTimeString) => {
  if (!utcIsoDateTimeString) return null;

  const localDateTime = DateTime.fromISO(utcIsoDateTimeString);

  const localJsDateTime = localDateTime.toJSDate();
  return localJsDateTime;
}

/**
 * @param {string} stringValue
 * @returns {boolean} True if the stringValue is a valid ISO 8601-compliant date only string
 * @example 
 * isIsoDate('2024-01-30') => true
 * isIsoDate('2024-1-30') => false
 * isIsoDate('2024-10-30T00:00:00.000Z') => false
 * isIsoDate('foobar') => false
 */
export const isIsoDate = (stringValue) => {
  const isoDateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD
  return isoDateOnlyRegex.test(stringValue);
};

/**
 * @param {string} utcIsoDateTimeString UTC ISO date & time string
 * @param {string} toFormat
 * @returns {string | null} Formatted local date only string
 * @example 
 * toFormattedDate('2024-10-30T00:00:00.000Z') => 'Oct 30, 2024'
 * toFormattedDate('2024-10-30T05:10:00.000Z') => 'Oct 30, 2024'
 * toFormattedDate('2024-10-30T00:00:00.000Z', DATE_FORMAT.US_SHORT) => '10/30/2024'
 * toFormattedDate('2024-10-30T00:00:00.000Z', DATE_FORMAT.ISO) => '2024-10-30'
 */
export const toFormattedDate = (utcIsoDateTimeString, toFormat = DATE_FORMAT.READABLE) => {
  if (!utcIsoDateTimeString) return null;

  const localDateTime = DateTime.fromISO(utcIsoDateTimeString);

  const localDateOnlyString = localDateTime.toFormat(toFormat);
  return localDateOnlyString;
}

/**
 * @param {number} seconds A number of seconds since 1970 UTC
 * @param {string} toFormat
 * @returns {string | null} Formatted local date only string
 * @example
 * toFormattedDateFromSeconds(1724995200) => 'Oct 30, 2024'
 */
export const toFormattedDateFromSeconds = (seconds, toFormat = DATE_FORMAT.READABLE) => {
  if (!seconds) return null;

  const localDateTime = DateTime.fromSeconds(+seconds);

  const localDateOnlyString = localDateTime.toFormat(toFormat);
  return localDateOnlyString;
}

/**
 * @param {Date} localJsDateTime A JavaScript Local date & time object
 * @param {string} toFormat
 * @returns {string | null} Formatted local date only string
 * @example
 * toFormattedDateFromJsDate(new Date('2024-10-30T00:00:00.000Z')) => 'Oct 30, 2024'
 */
export const toFormattedDateFromJsDate = (localJsDateTime, toFormat = DATE_FORMAT.READABLE) => {
  if (!localJsDateTime) return null;

  const localDateTime = DateTime.fromJSDate(localJsDateTime);

  const localDateOnlyString = localDateTime.toFormat(toFormat);
  return localDateOnlyString;
}

/**
 * @param {DateTime} localDateTime A luxon DateTime object
 * @param {string} toFormat
 * @returns {string | null} Formatted local date only string
 */
export const toFormattedDateFromLuxonDate = (localDateTime, toFormat = DATE_FORMAT.READABLE) => {
  if (!localDateTime) return null;

  const localDateOnlyString = localDateTime.toFormat(toFormat);
  return localDateOnlyString;
}

/**
 * @param {string} utcIsoDateTimeString UTC ISO date time string
 * @param {string} toFormat The format of the output time only string
 * @returns {string | null} Formatted time only string
 * @example
 * toFormattedTime('2024-10-30T00:00:00.000Z') => '12:00 AM'
 * toFormattedTime('2024-10-30T05:10:00.000Z') => '5:10 AM'
 */
export const toFormattedTime = (utcIsoDateTimeString, toFormat = 'hh:mm a') => {
  if (!utcIsoDateTimeString) return null;

  const localDateTime = DateTime.fromISO(utcIsoDateTimeString);

  const localTimeOnlyString = localDateTime.toFormat(toFormat);
  return localTimeOnlyString;
}

/**
 * @param {string} formattedDateString Formatted date only string
 * @param {string} fromFormat The format of the input date only string
 * @returns {string | null} ISO 8601-compliant date only string
 * @example
 * toIsoDate('Oct 30, 2024') => '2024-10-30'
 * toIsoDate('10/30/2024', DATE_FORMAT.US_SHORT) => '2024-10-30'
 */
export const toIsoDate = (formattedDateString, fromFormat = DATE_FORMAT.READABLE) => {
  if (!formattedDateString) return null;

  const localDateOnly = DateTime.fromFormat(formattedDateString, fromFormat);

  const isoDateOnlyString = localDateOnly.toISODate();
  return isoDateOnlyString;
}


/**
 * @param {string} isoToDateString ISO 8601-compliant date only string
 * @returns {number} The number of days between today and the given date
 * @example
 * getDaysFromToday('2024-10-20') => 15
 */
export const getDaysFromToday = (isoToDateString) => {
  const isoTodayDateString = getTodayIsoDate();

  const fromDate = DateTime.fromISO(isoTodayDateString);
  const toDate = DateTime.fromISO(isoToDateString);

  const diffInDays = toDate.diff(fromDate, 'days').days;
  return diffInDays > 0 ? diffInDays : 0;
};

/**
 * @param {DateTime} fromDate Luxon DateTime object
 * @param {DateTime} toDate Luxon DateTime object
 * @returns {number} The number of calendar days between the two dates
 * @example
 * getDiffInCalendarDays(DateTime.fromISO('2024-10-15'), DateTime.fromISO('2024-10-17')) => 2
 * getDiffInCalendarDays(DateTime.fromISO('2024-10-17'), DateTime.fromISO('2024-10-15')) => -2
 */
export const getDiffInCalendarDays = (fromDate, toDate) => {
  const diffInCalendarDays = toDate.diff(fromDate, 'days').days;
  return diffInCalendarDays;
};
